// Accessible tablist. Plan 24.
//
// ARIA tablist pattern:
//   - Container is `role="tablist"`.
//   - Each button is `role="tab"` with `aria-selected` + `tabIndex`.
//   - Arrow keys move focus within the tablist; Home/End jump to
//     edges; Enter/Space activate the focused tab.
//   - Consumer renders panels themselves based on `active`.
//
// We keep the tab surface component-only — no URL sync (plan 21
// already owns the router) and no auto-activation behaviour beyond
// "focus moves, click activates".

import { useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface TabItem<Id extends string = string> {
  id: Id;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<Id extends string = string> {
  items: ReadonlyArray<TabItem<Id>>;
  active: Id;
  onChange: (id: Id) => void;
  /** ARIA label for the tablist. */
  label?: string;
  /** Wrapping container className. */
  className?: string;
  variant?: 'pill' | 'underline' | 'sidebar';
}

const PILL_BASE =
  'flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900';

const UNDERLINE_BASE =
  'pb-4 px-2 text-sm font-bold uppercase tracking-widest transition-all relative focus-visible:outline-none';

// Sidebar items span their column, icon left, label, count badge pinned
// right. Active items get a stone-900 fill (matches the pill variant's
// "selected" tone).
const SIDEBAR_BASE =
  'w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900';

export function Tabs<Id extends string = string>({
  items,
  active,
  onChange,
  label = 'Tabs',
  className,
  variant = 'pill',
}: TabsProps<Id>) {
  const isVertical = variant === 'sidebar';
  const tabRefs = useRef(new Map<Id, HTMLButtonElement>());

  const focusTab = (id: Id) => {
    const el = tabRefs.current.get(id);
    if (el) el.focus();
  };

  const handleKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const idx = items.findIndex((i) => i.id === active);
    if (idx < 0) return;

    const go = (next: number) => {
      const clamped = (next + items.length) % items.length;
      const target = items[clamped];
      if (!target || target.disabled) return;
      onChange(target.id);
      focusTab(target.id);
    };

    // Both axes are wired so users can navigate a vertical sidebar with
    // Up/Down (primary) AND Left/Right (forgiving). The horizontal variants
    // keep their original behaviour.
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        go(idx + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        go(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        go(0);
        break;
      case 'End':
        e.preventDefault();
        go(items.length - 1);
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation={isVertical ? 'vertical' : 'horizontal'}
      className={cn(isVertical ? 'flex flex-col gap-1' : 'flex flex-wrap gap-4', className)}
      onKeyDown={handleKey}
    >
      {items.map((item) => {
        const isActive = item.id === active;
        const base =
          variant === 'pill' ? PILL_BASE : variant === 'underline' ? UNDERLINE_BASE : SIDEBAR_BASE;
        const activeClasses =
          variant === 'pill'
            ? isActive
              ? 'bg-stone-900 text-stone-50 shadow-lg'
              : 'bg-white text-stone-600 hover:bg-stone-50 border border-stone-200'
            : variant === 'underline'
              ? isActive
                ? 'text-stone-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-stone-900'
                : 'text-stone-400 hover:text-stone-600'
              : isActive
                ? 'bg-stone-900 text-stone-50 shadow-sm'
                : 'text-stone-600 hover:bg-stone-100';
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            ref={(el) => {
              if (el) tabRefs.current.set(item.id, el);
              else tabRefs.current.delete(item.id);
            }}
            onClick={() => !item.disabled && onChange(item.id)}
            className={cn(base, activeClasses, item.disabled && 'opacity-40 cursor-not-allowed')}
          >
            {item.icon && (
              <span className="inline-flex shrink-0" aria-hidden="true">
                {item.icon}
              </span>
            )}
            <span className={cn(isVertical && 'flex-1 text-left')}>{item.label}</span>
            {typeof item.count === 'number' && item.count > 0 && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                  isVertical ? '' : 'ml-1',
                  isActive ? 'bg-amber-500 text-white' : 'bg-amber-500/90 text-white'
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Props applied to the panel container. Consumers wrap their panel with
 * these so arrow-key focus flow and AT labelling line up.
 */
export function tabPanelProps<Id extends string>(id: Id) {
  return {
    role: 'tabpanel' as const,
    id: `tabpanel-${id}`,
    'aria-labelledby': `tab-${id}`,
    tabIndex: 0,
  };
}
